import React, { memo, useEffect, useRef, useState } from 'react';
import { Button as StorybookButton } from '@sunbeltrentals/sbr-global-components';
import DOMPurify from 'dompurify';
import { useCartState } from '../../../../contexts/cart';
import { useCartOperations } from '../../../cart/hooks/useCart';
import useCartAnalytics from '../../../../hooks/analytics/useCartAnalytics';
import { usePageType } from '../../../../hooks/usePageType';
import useToggle from '../../../../hooks/useToggle';
import AlertModalWithButtons from '../../../global/atoms/AlertModalWithButtons';
import { LoadingIndicator } from '../../../../aem-core-components';
import { logError } from '../../utils/logger';
import classes from './editQuoteBanner.module.scss';
import AlertCircle from '../../../../resources/images/alert-white-circle.svg';
import { ENV_CONFIG } from '../../../../constants/envConfig';
import { VARIABLE_CONFIG } from '../../../../constants/analyticsConstants/Variables';
import { editQuoteBannerDataLocator } from './dataLocator';

const EditQuoteBanner = props => {
    const [{ quoteData, isInEditQuoteMode }] = useCartState();
    const { onExitQuoteMode } = useCartOperations();
    const [{ triggerExitQuoteModalViewed, triggerExitQuoteModalActionEvent }] = useCartAnalytics();
    const pageType = usePageType();
    const [isModalOpen, toggleIsModalOpen] = useToggle();
    const [showLoader, setShowLoader] = useState(false);
    const editBannerRef = useRef(null);
    const quoteConfigs = JSON.parse(ENV_CONFIG.ECOM_QUOTE_CONFIGS);

    useEffect(() => {
        try {
            if (isInEditQuoteMode && editBannerRef?.current) {
                updatePageHeader();
            }
        } catch (error) {
            logError(error, false, 'EditQuoteBanner useEffect');
        }
    }, [isInEditQuoteMode, editBannerRef?.current]);

    const updatePageHeader = () => {
        try {
            // hiding 'My account' dropdown from header
            ENV_CONFIG.MINI_ACCOUNT?.classList?.add('my_account_dnone');
            // hiding eyebrow from header
            const headerEyebrow = ENV_CONFIG.HEADER_EYEBROW;
            if (headerEyebrow) {
                headerEyebrow.style.display = 'none';
            }
            updateStylingForSearchPage();
        } catch (error) {
            logError(error, false, 'updatePageHeader');
        }
    };

    const updateStylingForSearchPage = () => {
        if (pageType === VARIABLE_CONFIG.PAGE_TYPE.SEARCH_PAGE) {
            const headerElement = ENV_CONFIG.HEADER;
            const globalSearchHeader = ENV_CONFIG.GLOBAL_HEADER;

            const editBanner = editBannerRef?.current;
            const editBannerParent = editBanner?.parentElement;
            editBannerParent?.classList.add(classes.staticBanner); // making edit banner parent sticky

            if (globalSearchHeader) {
                const editBannerHeight = editBanner?.clientHeight || 0;

                // Adjust the top for the search header to move it down
                globalSearchHeader.style.setProperty('top', `${editBannerHeight}px`);

                if (headerElement) {
                    // adding padding-top on header element to move it down
                    const setNewPadding = globalSearchHeader.clientHeight + editBannerHeight;
                    headerElement.style.setProperty('z-index', 'initial');
                    headerElement.style.setProperty('padding-top', `${setNewPadding}px`, 'important');
                }
            }
        }
    };

    const handleCancelEditing = () => {
        toggleIsModalOpen();
        triggerExitQuoteModalViewed();
    };

    const handleExitQuoteMode = async () => {
        try {
            toggleIsModalOpen();
            setShowLoader(true);
            triggerExitQuoteModalActionEvent(VARIABLE_CONFIG.QUOTE.LEAVE_QUOTE_ACTION);
            await onExitQuoteMode();
        } catch (error) {
            logError(error, false, 'handleExitQuoteMode');
        }
        setShowLoader(false);
    };

    const handleModalClose = isContinueCTAClicked => {
        toggleIsModalOpen();
        triggerExitQuoteModalActionEvent(
            isContinueCTAClicked ? VARIABLE_CONFIG.QUOTE.CONTINUE_EDITING_ACTION : VARIABLE_CONFIG.QUOTE.DISMISS_ACTION
        );
    };

    const getModalContent = () => {
        const content = quoteConfigs?.ecomQuoteModalDescription?.replace(/#/g, `# ${quoteData?.orderId}`);
        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(content)
                }}></div>
        );
    };

    if (!isInEditQuoteMode) {
        return null;
    }

    return (
        <>
            <div className={classes.banner} ref={editBannerRef}>
                <div className={`container ${classes.container}`}>
                    <div className={classes.header}>
                        <div className={classes.text}>
                            <AlertCircle tabindex="-1" aria-hidden="true" />
                            <p>
                                {quoteConfigs?.editQuoteBannerContentLabel}
                                {quoteData?.orderId}
                            </p>
                        </div>
                        <StorybookButton
                            variant="primary"
                            customClassName={classes.cancelEditCta}
                            onClick={handleCancelEditing}
                            buttonAriaLabel={quoteConfigs?.editQuoteBannerCtaLabel}
                            dataTestId={editQuoteBannerDataLocator.edit_quote_banner_CTA}>
                            {quoteConfigs?.editQuoteBannerCtaLabel}
                        </StorybookButton>
                    </div>
                </div>
            </div>
            {isModalOpen && (
                <AlertModalWithButtons
                    isOpen
                    title={quoteConfigs?.ecomQuoteModalTitle}
                    warningQuestion={getModalContent()}
                    handleContinueClick={handleExitQuoteMode}
                    continueButtonText={quoteConfigs?.leaveEditQuoteCtaLabel}
                    continueButtonDataLocator={editQuoteBannerDataLocator.edit_quote_modal_continue_CTA}
                    handleCancelClick={handleModalClose}
                    cancelButtonText={quoteConfigs?.continueEditQuoteCtaLabel}
                    cancelButtonDataLocator={editQuoteBannerDataLocator.edit_quote_modal_cancel_CTA}
                    customClass="back_to_quote_modal_title"
                />
            )}
            {showLoader && (
                <div className={classes.loader}>
                    <LoadingIndicator global />
                </div>
            )}
        </>
    );
};

export default memo(EditQuoteBanner);
